import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Chip from "../Chip/Chip";
import Divider from "../Common/Divider";
import Button from "../Buttons/Button";
import CustomSelect from "../FormFields/Custom/CustomSelect";
import CustomSearch from "../FormFields/Custom/CustomSearch";
import CustomCheckboxNew from "../FormFields/Custom/CustomCheckboxNew";

const getTableStyles = (showCheckbox) => ({
  gridHeader: showCheckbox
    ? "grid grid-cols-[0.5fr_1.2fr_repeat(3,1fr)_0.5fr] gap-2 py-0.5 bg-tab-header-bg"
    : "grid grid-cols-[1.2fr_repeat(3,1fr)_0.5fr] gap-2 py-0.5 bg-tab-header-bg",
  gridHeaderSimple: showCheckbox
    ? "grid grid-cols-[0.5fr_1.2fr_0.5fr] gap-2 py-0.5 bg-tab-header-bg"
    : "grid grid-cols-[1.2fr_0.5fr] gap-2 py-0.5 bg-tab-header-bg",
  gridBody: "flex flex-col gap-0",
  gridBodyRow: showCheckbox
    ? "grid grid-cols-[0.5fr_1.2fr_repeat(3,1fr)_0.5fr] gap-2 py-1.5 border-b-[1px] border-gray-200"
    : "grid grid-cols-[1.2fr_repeat(3,1fr)_0.5fr] gap-2 py-1.5 border-b-[1px] border-gray-200",
  gridBodyRowSimple: showCheckbox
    ? "grid grid-cols-[0.5fr_1.2fr_0.5fr] gap-2 py-1.5 border-b-[1px] border-gray-200"
    : "grid grid-cols-[1.2fr_0.5fr] gap-2 py-1.5 border-b-[1px] border-gray-200",
  gridHeaderCell: "px-4 py-1 font-medium text-sm",
  gridBodyCell: "px-4 py-1 flex items-center",
});

const TEAM_CATEGORIES = [
  { id: 1, name: "Senior Team", designation: "senior" },
  { id: 2, name: "Junior Team", designation: "junior" },
  { id: 3, name: "Fresher Team", designation: "fresher" },
];

const DESIGNATION_COLORS = {
  senior: "!border-info-700 bg-info-50",
  junior: "!border-primary-700 bg-primary-50",
  fresher: "!border-invoiced-700 bg-invoiced-50",
  invalid: "!border-gray-500 bg-gray-50 cursor-not-allowed",
};

const CHIP_COLORS = {
  senior: "info",
  junior: "primary",
  fresher: "invoiced",
};

const options = [
  { label: "Showing 25 rows", value: "25" },
  { label: "Showing 50 rows", value: "50" },
  { label: "Showing 100 rows", value: "100" },
];

const getDesignationColors = (designation, isValid) => {
  if (!isValid) return DESIGNATION_COLORS.invalid;
  return DESIGNATION_COLORS[designation] || DESIGNATION_COLORS.invalid;
};

const groupTeamsByDesignation = (teamList) => {
  return TEAM_CATEGORIES.reduce((acc, category) => {
    acc[category.designation] = teamList.filter(
      (team) => team.designation === category.designation
    );
    return acc;
  }, {});
};

const mergeDuplicateTeams = (groupedTeams) => {
  const mergedResult = {};

  for (const [designation, teams] of Object.entries(groupedTeams)) {
    const map = new Map();

    for (const team of teams) {
      if (!map.has(team.id)) {
        map.set(team.id, { ...team });
      } else {
        const existing = map.get(team.id);
        existing.prospectCount += team.prospectCount || 0;
        existing.jobProspects = [
          ...(existing.jobProspects || []),
          ...(team.jobProspects || []),
        ];
      }
    }

    mergedResult[designation] = Array.from(map.values());
  }

  return mergedResult;
};

const generateArray = (length, min, max, total) => {
  const arr = Array(length).fill(0);
  let remaining = total;

  for (let i = 0; i < length; i++) {
    if (remaining <= 0) break;

    const assignable = Math.min(max, remaining);
    if (assignable >= min) {
      arr[i] = assignable;
      remaining -= assignable;
    }
  }

  return arr;
};

const AssignTable = ({
  data = [],
  output,
  type = "assign",
  shuffleType = "default",
  minEmails = 0,
  maxEmails = 100,
  dragndrop = true,
  hideToolBar = false,
  toolbarRight = {
    search: true,
    filter: true,
    add: [],
  },
  serverPagination = false,
  page = 1,
  limit = 10,
  onPageChange,
  onLimitChange,
  serverOnSearch,
  serverSearchValue,
  // Checkbox props
  showCheckbox = false,
  onSelectionChange,
  defaultSelectedRows = [],
  disableSelection = false,
  singleSelection = false,
}) => {
  const [draggedTeam, setDraggedTeam] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [dragOverZone, setDragOverZone] = useState(null);
  const [showRows, setShowRows] = useState(serverPagination ? limit : 25);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState(defaultSelectedRows);

  const bdmList = data.bdmList || [];
  const teamList = data.teamList || [];

  const totalEmails = teamList.reduce(
    (sum, team) => sum + (team.prospectCount || 0),
    0
  );

  const assignTeamsToBdms = () => {
    return bdmList.map((bdm) => {
      const teamsForBdm = teamList.filter((team) => team.assignedTo === bdm.id);

      const grouped = groupTeamsByDesignation(teamsForBdm);
      const mergedGrouped = mergeDuplicateTeams(grouped);

      const totalEmails = teamsForBdm.reduce(
        (sum, team) => sum + (team.prospectCount || 0),
        0
      );

      return {
        bdm,
        teams: mergedGrouped,
        totalEmails,
      };
    });
  };

  const assignEmailsEqually = () => {
    if (bdmList.length === 0 || totalEmails === 0) return [];

    if (type === "followup") {
      return bdmList.map((bdm, index) => {
        const teamsForBdm = teamList.filter(
          (team) => team.assignedTo === bdm.id
        );
        const grouped = groupTeamsByDesignation(teamsForBdm);

        const totalEmails = teamsForBdm.reduce(
          (sum, team) => sum + (team.prospectCount || 0),
          0
        );

        return {
          bdm: bdm,
          teams: grouped,
          totalEmails,
        };
      });
    }

    const emailsPerBdm = Math.floor(totalEmails / bdmList.length);
    const remainder = totalEmails % bdmList.length;

    return bdmList.map((bdm, index) => {
      const teamsForBdm = teamList.filter((team) => team.assignedTo === bdm.id);
      const grouped = groupTeamsByDesignation(teamsForBdm);

      return {
        bdm: bdm,
        teams: grouped,
        totalEmails: emailsPerBdm + (index < remainder ? 1 : 0),
      };
    });
  };

  const assignEmailsInRange = () => {
    if (!bdmList.length || totalEmails === 0) return [];

    const hasMin = minEmails != null && minEmails !== "";
    const hasMax = maxEmails != null && maxEmails !== "";

    if (type === "followup" && (!hasMin || !hasMax))
      return bdmList.map((bdm) => {
        const teams = teamList.filter((t) => t.assignedTo === bdm.id);
        return {
          bdm,
          teams: groupTeamsByDesignation(teams),
          totalEmails: teams.reduce((s, t) => s + (t.prospectCount || 0), 0),
        };
      });

    if (!hasMin || !hasMax)
      return bdmList.map((bdm) => ({
        bdm,
        teams: groupTeamsByDesignation(
          teamList.filter((t) => t.assignedTo === bdm.id)
        ),
        totalEmails: 0,
      }));

    const arrayVal = generateArray(
      bdmList.length,
      minEmails,
      maxEmails,
      totalEmails
    );

    const result = bdmList.map((bdm, i) => ({
      bdm,
      teams: groupTeamsByDesignation(
        teamList.filter((t) => t.assignedTo === bdm.id)
      ),
      totalEmails: arrayVal[i],
    }));

    return result;
  };

  const getAssignments = () => {
    switch (shuffleType) {
      case "equally":
        return assignEmailsEqually();
      case "range":
        return assignEmailsInRange();
      case "default":
      default:
        return assignTeamsToBdms();
    }
  };

  const bdmAssignments = assignments || getAssignments();

  // Pagination logic
  const startIndex = serverPagination
    ? (page - 1) * limit
    : (currentPage - 1) * showRows;
  const endIndex = serverPagination
    ? startIndex + limit
    : startIndex + showRows;
  const paginatedAssignments = serverPagination
    ? bdmAssignments
    : bdmAssignments.slice(startIndex, endIndex);
  const totalCount = bdmAssignments.length;
  const totalPages = Math.ceil(
    totalCount / (serverPagination ? limit : showRows)
  );

  const handleRowsPerPageChange = (value) => {
    const newRowsPerPage = Number(value);
    if (serverPagination) {
      onLimitChange?.(newRowsPerPage);
    } else {
      const newTotalPages = Math.ceil(totalCount / newRowsPerPage);
      const newPage = Math.min(
        Math.ceil((currentPage * showRows) / newRowsPerPage),
        newTotalPages
      );
      setShowRows(newRowsPerPage);
      setCurrentPage(newPage);
    }
  };

  const handlePageChange = (newPage) => {
    if (serverPagination) {
      onPageChange?.(newPage);
    } else {
      setCurrentPage(newPage);
    }
  };

  // Checkbox handlers
  const handleCheckboxChange = (bdmId, isChecked) => {
    if (disableSelection) return;

    if (singleSelection) {
      if (isChecked) {
        setSelectedRows([bdmId]);
      } else {
        setSelectedRows([]);
      }
    } else {
      if (isChecked) {
        setSelectedRows([...selectedRows, bdmId]);
      } else {
        setSelectedRows(selectedRows.filter((id) => id !== bdmId));
      }
    }
  };

  const toggleSelectAll = () => {
    if (disableSelection || singleSelection) return;

    const visibleBdmIds = paginatedAssignments.map(
      (assignment) => assignment.bdm.id
    );
    const allVisibleSelected = visibleBdmIds.every((id) =>
      selectedRows.includes(id)
    );

    if (allVisibleSelected) {
      setSelectedRows(selectedRows.filter((id) => !visibleBdmIds.includes(id)));
    } else {
      const newSelections = [...new Set([...selectedRows, ...visibleBdmIds])];
      setSelectedRows(newSelections);
    }
  };

  const someRowsSelected = () => {
    const visibleBdmIds = paginatedAssignments.map(
      (assignment) => assignment.bdm.id
    );
    const selectedVisibleRows = visibleBdmIds.filter((id) =>
      selectedRows.includes(id)
    );
    return (
      selectedVisibleRows.length > 0 &&
      selectedVisibleRows.length < visibleBdmIds.length
    );
  };

  useEffect(() => {
    setAssignments(getAssignments());
  }, [shuffleType, minEmails, maxEmails]);

  useEffect(() => {
    if (assignments && output) {
      output(assignments);
    }
  }, [assignments, output]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (defaultSelectedRows.length > 0) {
      setSelectedRows(defaultSelectedRows);
    }
  }, [defaultSelectedRows]);

  const handleDragStart = (team, fromBdmId, designation) => {
    if (shuffleType !== "default") return;
    setDraggedTeam({ team, fromBdmId, designation });
  };

  const handleDragEnd = () => {
    setDraggedTeam(null);
    setDragOverZone(null);
  };

  const handleDrop = (toBdmId, toDesignation) => {
    if (shuffleType !== "default") return;

    if (!draggedTeam) {
      return;
    }

    if (draggedTeam.designation !== toDesignation) {
      return;
    }

    const { team, fromBdmId } = draggedTeam;

    if (fromBdmId === toBdmId) {
      return;
    }

    setAssignments((prevAssignments) => {
      const newAssignments = [...prevAssignments];

      const fromBdmIndex = newAssignments.findIndex(
        (a) => a.bdm.id === fromBdmId
      );
      if (fromBdmIndex !== -1) {
        newAssignments[fromBdmIndex] = {
          ...newAssignments[fromBdmIndex],
          teams: {
            ...newAssignments[fromBdmIndex].teams,
            [toDesignation]: newAssignments[fromBdmIndex].teams[
              toDesignation
            ].filter((t) => t.id !== team.id),
          },
          totalEmails:
            newAssignments[fromBdmIndex].totalEmails -
            (team.prospectCount || 0),
        };
      }

      const toBdmIndex = newAssignments.findIndex((a) => a.bdm.id === toBdmId);
      if (toBdmIndex !== -1) {
        newAssignments[toBdmIndex] = {
          ...newAssignments[toBdmIndex],
          teams: {
            ...newAssignments[toBdmIndex].teams,
            [toDesignation]: [
              ...newAssignments[toBdmIndex].teams[toDesignation],
              team,
            ],
          },
          totalEmails:
            newAssignments[toBdmIndex].totalEmails + (team.prospectCount || 0),
        };
      }

      return newAssignments;
    });
  };

  return (
    <div className="flex flex-col gap-0 w-full bg-white flex-1">
      {/* Toolbar */}
      {!hideToolBar && (
        <div className="toolbar py-2 flex justify-between items-center text-sm">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center py-2 gap-2">
              {/* <span>
                {(serverPagination ? (page - 1) * limit : startIndex) + 1}-
                {Math.min(
                  serverPagination ? page * limit : endIndex,
                  totalCount
                )}
              </span>
              <span> of {totalCount}</span> */}
              {/* modidfied this to get 0-0 of 0 instead of 1-0 of 0 in pagination when there is no data */}
              <span>
                {totalCount === 0
                  ? 0
                  : (serverPagination ? (page - 1) * limit : startIndex) + 1}
                -
                {Math.min(
                  serverPagination ? page * limit : endIndex,
                  totalCount,
                )}
              </span>
              <span> of {totalCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                type="icon"
                variant="text"
                color="black"
                icon="chevron_left"
                onClick={() =>
                  handlePageChange(
                    serverPagination ? page - 1 : Math.max(currentPage - 1, 1)
                  )
                }
                disabled={serverPagination ? page === 1 : currentPage === 1}
                data-dropdown-persist="true"
              />
              <Button
                size="sm"
                icon="chevron_right"
                type="icon"
                variant="text"
                color="black"
                onClick={() =>
                  handlePageChange(
                    serverPagination
                      ? page + 1
                      : Math.min(currentPage + 1, totalPages)
                  )
                }
                disabled={
                  serverPagination
                    ? page >= Math.ceil(totalCount / limit)
                    : endIndex >= totalCount
                }
                data-dropdown-persist="true"
              />
            </div>
            <div className=" flex ">
              <CustomSelect
                size="sm"
                placeholder="Select rows"
                options={options}
                selected={String(serverPagination ? limit : showRows)}
                onChange={handleRowsPerPageChange}
                data-dropdown-persist="true"
              />
              {/* {showCheckbox && !disableSelection && selectedRows.length > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <div>
                    {selectedRows.length}{" "}
                    {selectedRows.length === 1 ? "item" : "items"} selected
                  </div>
                  <div>
                    <Button
                      size="xs-sm"
                      type="icon"
                      variant="text"
                      color="danger"
                      icon="close"
                      data-dropdown-persist="true"
                      onClick={() => setSelectedRows([])}
                    />
                  </div>
                </div>
              )} */}
            </div>
          </div>
          <div className="toolbar-right flex items-center gap-1 mr-3">
            {toolbarRight?.search && (
              <div>
                <CustomSearch
                  size="sm"
                  value={serverSearchValue}
                  onChange={serverOnSearch}
                  keyname="search"
                  rounded="rounded"
                  closeBtn
                />
              </div>
            )}
            {toolbarRight?.filter && (
              <div>
                <Button
                  type="icon"
                  variant="text"
                  color="black"
                  icon="filter_alt"
                  onClick={() => {
                    // Add filter functionality here
                  }}
                />
              </div>
            )}
            <div className="flex items-center gap-1">
              {toolbarRight?.add &&
                toolbarRight.add.map((item, key) => (
                  <div key={`toolbar-add-${key}`}>
                    {item.component ? (
                      item.component
                    ) : (
                      <Button
                        icon={item?.icon}
                        type="icon"
                        variant="text"
                        color="black"
                        onClick={item?.onClick}
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      <TableHeader
        draggedTeam={draggedTeam}
        shuffleType={shuffleType}
        showCheckbox={showCheckbox}
        onSelectAll={toggleSelectAll}
        allSelected={
          paginatedAssignments.length > 0 &&
          paginatedAssignments.every((assignment) =>
            selectedRows.includes(assignment.bdm.id)
          )
        }
        indeterminate={someRowsSelected()}
        disableSelection={disableSelection}
        singleSelection={singleSelection}
      />
      <Divider />
      <TableBody
        assignments={paginatedAssignments}
        draggedTeam={draggedTeam}
        dragOverZone={dragOverZone}
        setDragOverZone={setDragOverZone}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDrop={handleDrop}
        shuffleType={shuffleType}
        dragndrop={dragndrop}
        showCheckbox={showCheckbox}
        selectedRows={selectedRows}
        onCheckboxChange={handleCheckboxChange}
        disableSelection={disableSelection}
      />
    </div>
  );
};

const TableHeader = ({
  draggedTeam,
  shuffleType,
  showCheckbox,
  onSelectAll,
  allSelected,
  indeterminate,
  disableSelection,
  singleSelection,
}) => {
  const isSimpleMode = shuffleType === "equally" || shuffleType === "range";
  const TABLE_STYLES = getTableStyles(showCheckbox);

  return (
    <div
      className={
        isSimpleMode ? TABLE_STYLES.gridHeaderSimple : TABLE_STYLES.gridHeader
      }
    >
      {showCheckbox && (
        <div
          className={`${TABLE_STYLES.gridHeaderCell} flex items-center justify-center`}
        >
          {!singleSelection && !disableSelection && (
            <CustomCheckboxNew
              id="select-all-checkbox"
              checked={allSelected}
              indeterminate={indeterminate}
              onChange={onSelectAll}
            />
          )}
        </div>
      )}
      <div
        className={`${TABLE_STYLES.gridHeaderCell} flex items-center justify-between`}
      >
        <span>BDM</span>
      </div>
      {!isSimpleMode &&
        TEAM_CATEGORIES.map((category) => (
          <div
            key={category.id}
            className={`${TABLE_STYLES.gridHeaderCell} ${
              draggedTeam && draggedTeam.designation === category.designation
                ? "bg-blue-100 text-blue-800"
                : ""
            }`}
          >
            {category.name}
          </div>
        ))}
      <div className={TABLE_STYLES.gridHeaderCell}>
        {isSimpleMode ? "Assigned Emails" : "Total Emails"}
      </div>
    </div>
  );
};

const TableBody = ({
  assignments,
  draggedTeam,
  dragOverZone,
  setDragOverZone,
  handleDragStart,
  handleDragEnd,
  handleDrop,
  shuffleType,
  dragndrop,
  showCheckbox,
  selectedRows,
  onCheckboxChange,
  disableSelection,
}) => {
  const TABLE_STYLES = getTableStyles(showCheckbox);

  return (
    <div className={TABLE_STYLES.gridBody}>
      {assignments.map((assignment) => (
        <BdmRow
          key={assignment.bdm.id}
          assignment={assignment}
          draggedTeam={draggedTeam}
          dragOverZone={dragOverZone}
          setDragOverZone={setDragOverZone}
          handleDragStart={handleDragStart}
          handleDragEnd={handleDragEnd}
          handleDrop={handleDrop}
          shuffleType={shuffleType}
          dragndrop={dragndrop}
          showCheckbox={showCheckbox}
          isSelected={selectedRows.includes(assignment.bdm.id)}
          onCheckboxChange={onCheckboxChange}
          disableSelection={disableSelection}
        />
      ))}
    </div>
  );
};

const BdmRow = ({
  assignment,
  draggedTeam,
  dragOverZone,
  setDragOverZone,
  handleDragStart,
  handleDragEnd,
  handleDrop,
  shuffleType,
  dragndrop,
  showCheckbox,
  isSelected,
  onCheckboxChange,
  disableSelection,
}) => {
  const isSimpleMode = shuffleType === "equally" || shuffleType === "range";
  const TABLE_STYLES = getTableStyles(showCheckbox);

  return (
    <div
      className={`${
        isSimpleMode ? TABLE_STYLES.gridBodyRowSimple : TABLE_STYLES.gridBodyRow
      } ${isSelected ? "bg-primary-50" : ""}`}
    >
      {showCheckbox && (
        <div
          className={`${TABLE_STYLES.gridBodyCell} justify-center`}
          onClick={(e) => e.stopPropagation()}
        >
          <CustomCheckboxNew
            id={`row-checkbox-${assignment.bdm.id}`}
            checked={isSelected}
            onChange={(e) =>
              onCheckboxChange(assignment.bdm.id, e.target.checked)
            }
            disabled={disableSelection}
          />
        </div>
      )}
      <div className={TABLE_STYLES.gridBodyCell}>
        <div className="flex flex-col gap-2">
          <div className="body-one font-medium">{assignment.bdm.name}</div>
          <div className="body-two">{assignment.bdm.email}</div>
        </div>
      </div>

      {!isSimpleMode &&
        TEAM_CATEGORIES.map((category) => (
          <TeamColumn
            key={category.id}
            category={category}
            assignment={assignment}
            draggedTeam={draggedTeam}
            dragOverZone={dragOverZone}
            setDragOverZone={setDragOverZone}
            handleDragStart={handleDragStart}
            handleDragEnd={handleDragEnd}
            handleDrop={handleDrop}
            dragndrop={dragndrop}
          />
        ))}

      <div className={TABLE_STYLES.gridBodyCell}>
        <div className="flex flex-col">
          <div className="text-lg font-semibold">{assignment.totalEmails}</div>
          {!isSimpleMode && (
            <div className="text-xs text-gray-500">
              {assignment.teams.senior.length +
                assignment.teams.junior.length +
                assignment.teams.fresher.length}{" "}
              teams
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TeamColumn = ({
  category,
  assignment,
  draggedTeam,
  dragOverZone,
  setDragOverZone,
  handleDragStart,
  handleDragEnd,
  handleDrop,
  dragndrop = true,
}) => {
  const TABLE_STYLES = getTableStyles(false);
  const zoneKey = `${assignment.bdm.id}-${category.designation}`;
  const isValidDrop =
    draggedTeam && draggedTeam.designation === category.designation;

  return (
    <motion.div
      className={`${
        TABLE_STYLES.gridBodyCell
      } min-h-[60px] rounded-lg border-2 border-dashed border-transparent transition-all duration-200 ${
        dragOverZone === zoneKey
          ? getDesignationColors(category.designation, isValidDrop)
          : ""
      } ${
        dragOverZone === zoneKey && !isValidDrop ? "cursor-not-allowed" : ""
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOverZone(zoneKey);
        if (draggedTeam) {
          if (isValidDrop) {
            e.dataTransfer.dropEffect = "move";
          } else {
            e.dataTransfer.dropEffect = "none";
          }
        }
      }}
      onDragEnter={(e) => {
        if (!dragndrop) return;
        e.preventDefault();
      }}
      onDragLeave={(e) => {
        if (!dragndrop) return;
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setDragOverZone(null);
        }
      }}
      onDrop={(e) => {
        if (!dragndrop) return;
        e.preventDefault();
        setDragOverZone(null);
        handleDrop(assignment.bdm.id, category.designation);
      }}
    >
      <div className="flex gap-x-2 gap-y-1 flex-wrap">
        <AnimatePresence>
          {assignment.teams[category.designation].length === 0 ? (
            <EmptyTeamSlot
              draggedTeam={draggedTeam}
              categoryDesignation={category.designation}
            />
          ) : (
            assignment.teams[category.designation].map((team) => (
              <TeamChip
                key={team.id}
                team={team}
                category={category}
                assignment={assignment}
                draggedTeam={draggedTeam}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
              />
            ))
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const EmptyTeamSlot = ({ draggedTeam, categoryDesignation }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="text-gray-400 text-sm italic py-2"
  >
    {draggedTeam && draggedTeam.designation === categoryDesignation
      ? "Drop here"
      : "No teams assigned"}
  </motion.div>
);

const TeamChip = ({
  team,
  category,
  assignment,
  draggedTeam,
  handleDragStart,
  handleDragEnd,
}) => (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.8 }}
    whileHover={{ scale: 1.05 }}
    className="cursor-grab active:cursor-grabbing"
    draggable
    onDragStart={(e) => {
      handleDragStart(team, assignment.bdm.id, category.designation);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", "");
    }}
    onDragEnd={handleDragEnd}
    style={{
      opacity: draggedTeam && draggedTeam.team.id === team.id ? 0.5 : 1,
    }}
  >
    <Chip
      variant="outlined"
      label={`${team.name} - ${team.prospectCount}`}
      color={CHIP_COLORS[team.designation]}
    />
  </motion.div>
);

export default AssignTable;
